import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../utils/LanguageContext';
import LanguageSelector from '../layout/LanguageSelector';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const MainNav = () => {
  const { t, language, setLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlLang = params.get('lang');
    if (urlLang && urlLang !== language) {
      setLanguage(urlLang);
    }
  }, [location.search, language, setLanguage]);

  const getLanguageUrl = (path, lang) => {
    const params = new URLSearchParams(location.search);
    params.set('lang', lang);
    return `${path}?${params.toString()}`;
  };

  const navigation = [
    { name: 'guide', href: '/' },
    { name: 'modules', href: '/modules' },
    { name: 'about', href: '/about' }
  ];

  const logoUrl = language === 'cs'
    ? 'https://medite-sss-infpro-182059100462.s3.eu-west-1.amazonaws.com/jirhyl/logo_digital_horizon_small_white_transparent/source__standard_upload/logo_digital_horizon_small_white_transparent.svg'
    : 'https://medite-sss-infpro-182059100462.s3.eu-west-1.amazonaws.com/jirhyl/logo_digital_horizon_small_white_transparent/source__standard_upload/logo_digital_horizon_small_white_transparent.svg';

  const handleLanguageChange = (newLang) => {
    const params = new URLSearchParams(location.search);
    params.set('lang', newLang);
    navigate(`${location.pathname}?${params.toString()}`);
  };

  return (
    <nav className="bg-white shadow relative">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-20 items-center justify-between">
          <Link to={getLanguageUrl('/', language)} className="flex flex-shrink-0 items-center">
            <img src={logoUrl} alt="VSB-TUO Logo" className="h-24 w-auto" />
          </Link>
          
          <div className="hidden lg:flex lg:space-x-8">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={getLanguageUrl(item.href, language)}
                className={({ isActive }) =>
                  `inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium ${
                    isActive
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`
                }
              >
                {t(`nav.${item.name}`)}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-600"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
           
            <div className="relative z-20">
              <LanguageSelector onLanguageChange={handleLanguageChange} />
            </div>
          </div>
        </div>
      </div>

      <div className={`lg:hidden absolute w-full bg-white shadow-lg z-10 ${isOpen ? 'block' : 'hidden'}`}>
        <div className="space-y-1 pb-3 pt-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={getLanguageUrl(item.href, language)}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
                  isActive
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                }`
              }
            >
              {t(`nav.${item.name}`)}
            </NavLink>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default MainNav;